/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
// we need to use tabindex on popup ^^ so it could be interactable right away
// when it's appears
import React, { useState, useMemo, useRef, useEffect } from 'react';
import Dropdown from 'react-dropdown';
import { TouchScrollable } from 'react-scrolllock';
import get from 'lodash.get';

import Button from '../buttons/Button';
import CloseButton from '../buttons/CloseButton';
import Popup from './Popup';
import RadioButton from '../buttons/RadioButton';

import { ASSESSMENT_TYPE, MEASURE_BY_SKILL_NAME } from '../../constants/assessments';
import { useAppContext } from '../contexts/AppContext';
import { usePopupContext } from '../contexts/PopupContext';
import { constructIdel2Link, linkOnClickHandler, PAGE_TYPE } from '../navigation/navigation';
import { FORM_SOURCE } from '../../constants/forms';
import { IDEL2 } from '../../constants/applications';
import { getGroupedForms, getSortedForms } from '../utils/prepareFormsData';
import { getMeasureResults } from '../utils/alternateFormsHelpers';
import { getAllStudentResults, getStudentResults } from '../utils/studentsListHelpers';
import { getNextForm } from '../recommendation-rules/getNextForm';
import { timesFormWasUsedInResults } from '../utils/popupDataHelper';
import { formLegendOption, getFormOptions, isDigitalFormsPopupEnabled } from './utils';
import { ALT_FORM_MESSAGE as MESSAGE } from '../../constants/popups';

import { openOnEnterKeyPressedOnDropdown } from '../utils/dropDownHelpers';

const getDropdownItemData = (forms, studentResultsByApp) =>
    forms.map(({ uid, sequence, genre }) => ({
        formUid: uid,
        formName: `Form ${sequence}`,
        timesUsedInResults: timesFormWasUsedInResults(uid, studentResultsByApp),
        genre,
    }));

const LecturaAltFormSelectionPopup = () => {
    const {
        forms: {
            [IDEL2]: { [ASSESSMENT_TYPE.PM]: rawForms },
        },
        classId,
        allResults,
        checkAndGetSessionStatus,
    } = useAppContext();

    const {
        userPopupsSettings: { isDigitalPopupEnabled },
        params: { grade, skillName, period, measureName, studentId },
        actions: {
            hideLecturaAltFormSelectionPopup,
            showDigitalPopup,
            showLecturaAltFormSelectionPopup,
        },
    } = usePopupContext();

    const dropdownRef = useRef(null);

    const isDevEnv = process.env.NODE_ENV === 'development';
    const dashboardPrefix = isDevEnv ? '' : 'dashboard-';

    const formSelectionDropdownSelector = `.${dashboardPrefix}alt-form-selection-dropdown-wrapper`;

    const formSelectionDropdown = document.querySelector(formSelectionDropdownSelector);

    useEffect(() => {
        if (formSelectionDropdown) {
            formSelectionDropdown.addEventListener(
                'keydown',
                openOnEnterKeyPressedOnDropdown(dropdownRef, formSelectionDropdown, true),
            );
        }

        return () => {
            if (formSelectionDropdown) {
                formSelectionDropdown.removeEventListener(
                    'keydown',
                    openOnEnterKeyPressedOnDropdown(dropdownRef, formSelectionDropdown, true),
                );
            }
        };
    }, [formSelectionDropdown]);

    const studentResults = useMemo(
        () =>
            getStudentResults(
                { [IDEL2]: get(allResults, IDEL2, {}) },
                studentId,
                getAllStudentResults,
            ),
        [allResults, studentId],
    );
    const forms = getGroupedForms(rawForms, skillName, measureName)[grade];

    const sortedFormsBySequence = getSortedForms(forms);

    const measureResults = useMemo(() => getMeasureResults(studentResults[IDEL2], skillName), [
        skillName,
        studentResults,
    ]);

    const [selectedFormSource, setSelectedFormSource] = useState(FORM_SOURCE.BM.id);

    const [selectedFormUid, setSelectedFormUid] = useState(() =>
        get(getNextForm(forms, measureResults), 'uid', ''),
    );

    const handleFormSourceChange = event => setSelectedFormSource(event.target.value);

    const handleFormSelected = event => setSelectedFormUid(event.value);

    const isPMselected = selectedFormSource === FORM_SOURCE.PM.id;

    const getQueryParams = () => {
        return {
            ...(isPMselected && {
                formUid: selectedFormUid,
            }),
            period,
            classId,
        };
    };

    const selectedForm = sortedFormsBySequence.find(form => form.uid === selectedFormUid);
    const sequence = get(selectedForm, 'sequence');

    const dropdownItemData = getDropdownItemData(sortedFormsBySequence, studentResults[IDEL2]);

    const queryParams = getQueryParams();

    const navigateToAssessment = e => {
        const assessmentLink = constructIdel2Link(
            studentId,
            MEASURE_BY_SKILL_NAME[skillName],
            PAGE_TYPE.REMINDERS,
            queryParams,
        );

        if (
            isDigitalFormsPopupEnabled(sequence, measureName, isDigitalPopupEnabled) &&
            isPMselected
        ) {
            showDigitalPopup({
                assessmentLink,
                grade,
                studentId,
                skillName,
                period,
                measureName,
                onCancelHandler: showLecturaAltFormSelectionPopup,
            });
        } else {
            linkOnClickHandler(assessmentLink, checkAndGetSessionStatus)(e);
        }
    };

    const onOKButtonClick = () => {
        hideLecturaAltFormSelectionPopup();
        navigateToAssessment();
    };

    const formOptions = [
        formLegendOption,
        ...getFormOptions(dropdownItemData, measureName, sortedFormsBySequence),
    ];

    const selectedFormOption = formOptions.find(option => option.value === selectedFormUid);

    return (
        <Popup
            header={
                <>
                    <p>Alternate Form Selection</p>
                    <CloseButton onClick={hideLecturaAltFormSelectionPopup} />
                </>
            }
            message={
                <>
                    {MESSAGE[0]}
                    <br />
                    {MESSAGE[1]}
                    <p>
                        <RadioButton
                            value={FORM_SOURCE.BM.id}
                            id={FORM_SOURCE.BM.id}
                            name={FORM_SOURCE.BM.name}
                            isChecked={selectedFormSource === FORM_SOURCE.BM.id}
                            onChange={handleFormSourceChange}
                        />
                        <RadioButton
                            value={FORM_SOURCE.PM.id}
                            id={FORM_SOURCE.PM.id}
                            name={FORM_SOURCE.PM.name}
                            isChecked={selectedFormSource === FORM_SOURCE.PM.id}
                            onChange={handleFormSourceChange}
                        />
                    </p>
                    <TouchScrollable>
                        <div
                            className='dashboard-alt-form-selection-dropdown-wrapper'
                            tabIndex={selectedFormSource === FORM_SOURCE.PM.id ? 0 : -1}
                        >
                            <Dropdown
                                controlClassName='dashboard-form-selection-dropdown'
                                arrowClassName='dashboard-form-selection-dropdown-arrow'
                                menuClassName='dashboard-form-selection-dropdown-menu'
                                placeholder="Select form"
                                onChange={handleFormSelected}
                                disabled={selectedFormSource === FORM_SOURCE.BM.id}
                                options={formOptions}
                                value={selectedFormOption}
                                ref={dropdownRef}
                            />
                        </div>
                    </TouchScrollable>
                </>
            }
            buttons={[<Button key="ok-button" onClick={onOKButtonClick} />]}
            cssClass="alt-form-selection-popup"
        />
    );
};

export default LecturaAltFormSelectionPopup;
